import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { XMarkIcon, CubeIcon, CalendarIcon } from '@heroicons/react/24/outline';
import { Producto } from '../../types';
import { useCategoriasStore } from '../../stores/useCategoriasStore';
import { useColeccionesStore } from '../../stores/useColeccionesStore';
import { useUbicacionesStore } from '../../stores/useUbicacionesStore';
import { useAuthStore } from '../../stores/useAuthStore';
import { useEmpresaRolePermissions } from '../../hooks/useEmpresaRolePermissions';
import { supabase } from '../../lib/supabase';
import { ImageGallery } from './ImageGallery';
import { Trash2 } from 'lucide-react';
import { filterValidImageUrls } from '../../stores/useProductosStore';
import { TypeSelector, type TipoOferta } from './TypeSelector';
import { HorariosBuilder, type HorarioSlot } from './HorariosBuilder';
import { AtributosEditor } from './AtributosEditor';
import { atributosRowsToDbPayload, normalizeAtributosFromDb } from '../../utils/productoAtributos';
import type { ProductoAtributoRow } from '../../types/productoAtributos';

interface ModalProductoProps {
  producto?: Producto | null;
  modo: 'crear' | 'editar';
  onGuardar: (datosProducto: Partial<Producto>, options?: { pendingImageFiles?: File[] }) => void;
  onCancelar: () => void;
  /** Duplicar ítem actual (solo edición) y abrir el clon en el padre */
  onDuplicar?: (producto: Producto) => void | Promise<void>;
}

/** Parse precio desde texto del input; cadena vacía → NaN (no forzar 0). */
function parsePrecioField(raw: string): number {
  const t = raw.trim().replace(',', '.');
  if (t === '' || t === '.' || t === '-') return NaN;
  const n = parseFloat(t);
  return Number.isFinite(n) ? n : NaN;
}

const ModalProducto: React.FC<ModalProductoProps> = ({ producto, modo, onGuardar, onCancelar, onDuplicar }) => {
  const navigate = useNavigate();
  const { currentEmpresa } = useAuthStore();
  const { canSeeCosto, canEditPrecio, canEditProducto } = useEmpresaRolePermissions();
  const readOnlyForm = !canEditProducto;
  const precioVentaLocked = readOnlyForm || !canEditPrecio;
  const { categorias, fetchCategorias, crearCategoria, isLoading: categoriasLoading } = useCategoriasStore();
  const { ubicaciones, fetchUbicaciones, isLoading: ubicacionesLoading } = useUbicacionesStore();
  const { colecciones, fetchColecciones, isLoading: coleccionesLoading } = useColeccionesStore();

  // Fetch categories and locations on mount
  const [productoColeccionIds, setProductoColeccionIds] = useState<string[]>([]);

  useEffect(() => {
    if (currentEmpresa?.id) {
      void cargarCategorias();
      fetchUbicaciones(currentEmpresa.id);
      void fetchColecciones(currentEmpresa.id);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentEmpresa?.id, fetchCategorias, fetchUbicaciones, fetchColecciones]);

  useEffect(() => {
    if (modo === 'editar' && producto?.id) {
      void supabase
        .from('coleccion_productos')
        .select('coleccion_id')
        .eq('producto_id', producto.id)
        .then(({ data, error: qErr }) => {
          if (qErr) return;
          setProductoColeccionIds((data || []).map((r) => r.coleccion_id));
        });
    } else {
      setProductoColeccionIds([]);
    }
  }, [modo, producto?.id]);
  
  // Get active categories for dropdown
  const categoriasActivas = categorias.filter(c => c.activa);
  const normCat = (v?: string | null) => (v ?? '').trim().toLowerCase();
  /** Categoría que el producto ya tenía guardada (solo en edición). */
  const categoriaOriginal = modo === 'editar' ? normCat(producto?.categoria) : '';
  /** Válida si es activa de la empresa, o inactiva pero ya era la del producto en edición. */
  const esCategoriaValida = (nombre: string) => {
    const n = normCat(nombre);
    if (!n) return false;
    return categorias.some(
      (c) => normCat(c.nombre) === n && (c.activa || (categoriaOriginal !== '' && n === categoriaOriginal))
    );
  };
  const categoriaInactiva = (nombre: string) =>
    categorias.some((c) => !c.activa && normCat(c.nombre) === normCat(nombre));
  const hayOpcionExacta = (nombre: string) => categoriasActivas.some((c) => c.nombre === nombre);
  
  const [formData, setFormData] = useState<{
    nombre: string;
    sku: string;
    barcode: string;
    descripcion: string;
    categoria: string;
    precioCompra: string;
    precioVenta: string;
    stock: string;
    stockMinimo: number;
    inventory_type: 'tracked' | 'unlimited';
    unidadMedida: string;
    estado: string;
    imagen: string;
    // Multiple images (URLs for edit; new product uses pendingImageFiles)
    imagenes: string[];
    ivaPorcentaje: number;
    precioConIva: number;
    precioSinIva: number;
    valorIva: number;
    mostrarEnCatalogo: boolean;
    mostrarPrecio: boolean;
    mostrarStock: boolean;
    destacado: boolean;
    disponibleOnline: boolean;
    ordenCatalogo: number;
    tags: string[];
    tiempoPreparacion: string;
    ubicacion: string;
    // Scheduling Configuration
    isBookable: boolean;
    requiresTimeSlot: boolean;
    customDurationMinutes: number | null;
    fulfillmentOptions: string[];
    // Subscription product fields
    esSuscripcion: boolean;
    frecuenciaCobro: string;
    clasesIncluidas: number | null;
    duracionDias: number;
    autoRenovar: boolean;
    generaQrCode: boolean;
    requiereCheckin: boolean;
    tipoOferta: TipoOferta;
    accesoLibre: boolean;
    incluyeGrupal: boolean;
    incluyePrivada: boolean;
    tipoListado: '' | 'venta' | 'arriendo' | 'oferta';
    ubicacionListado: string;
    areaM2: number | null;
    atributosRows: ProductoAtributoRow[];
  }>({
    nombre: '',
    sku: '',
    barcode: '',
    descripcion: '',
    categoria: '',
    precioCompra: '',
    precioVenta: '',
    stock: '',
    stockMinimo: 0,
    inventory_type: 'tracked',
    unidadMedida: 'Unidad',
    estado: 'ACTIVO',
    imagen: '',
    imagenes: [],
    // IVA Configuration
    ivaPorcentaje: 0, // Start empty - owner decides the IVA amount
    precioConIva: 0,
    precioSinIva: 0,
    valorIva: 0,
    // Catalog Configuration
    mostrarEnCatalogo: true,
    mostrarPrecio: true,
    mostrarStock: true,
    destacado: false,
    disponibleOnline: true,
    ordenCatalogo: 0,
    tags: [],
    tiempoPreparacion: '',
    ubicacion: '',
    // Scheduling Configuration
    isBookable: false,
    requiresTimeSlot: true,
    customDurationMinutes: null,
    fulfillmentOptions: ['pickup'],
    // Subscription
    esSuscripcion: false,
    frecuenciaCobro: 'mensual',
    clasesIncluidas: null,
    duracionDias: 30,
    autoRenovar: true,
    generaQrCode: true,
    requiereCheckin: true,
    tipoOferta: 'producto',
    accesoLibre: false,
    incluyeGrupal: false,
    incluyePrivada: false,
    tipoListado: '',
    ubicacionListado: '',
    areaM2: null,
    atributosRows: []
  });

  const [horarios, setHorarios] = useState<HorarioSlot[]>([]);

  const [errores, setErrores] = useState<Record<string, string>>({});
  const [mostrarNuevaCategoria, setMostrarNuevaCategoria] = useState(false);
  const [nuevaCategoriaNombre, setNuevaCategoriaNombre] = useState('');
  const [creandoCategoria, setCreandoCategoria] = useState(false);
  const [categoriasCargadas, setCategoriasCargadas] = useState(false);
  const [errorCargaCategorias, setErrorCargaCategorias] = useState('');

  const cargarCategorias = async () => {
    setErrorCargaCategorias('');
    setCategoriasCargadas(false);
    await fetchCategorias();
    const err = useCategoriasStore.getState().error;
    if (err) {
      setErrorCargaCategorias('No se pudieron cargar las categorías.');
    } else {
      setCategoriasCargadas(true);
    }
  };
  /** Solo validamos contra la lista cuando se cargó correctamente. */
  const listaCategoriasLista = categoriasCargadas && !categoriasLoading;
  const [errorNuevaCategoria, setErrorNuevaCategoria] = useState('');

  const handleCrearCategoriaInline = async () => {
    if (!categoriasCargadas) return;
    const nombre = nuevaCategoriaNombre.trim();
    if (!nombre) {
      setErrorNuevaCategoria('Escribe el nombre de la categoría');
      return;
    }
    const existente = categorias.find((c) => normCat(c.nombre) === normCat(nombre));
    if (existente) {
      handleChange('categoria', existente.nombre);
      setMostrarNuevaCategoria(false);
      setNuevaCategoriaNombre('');
      setErrorNuevaCategoria('');
      return;
    }
    setCreandoCategoria(true);
    setErrorNuevaCategoria('');
    try {
      await crearCategoria({ nombre, activa: true });
      const errCrear = useCategoriasStore.getState().error;
      if (errCrear) {
        useCategoriasStore.setState({ error: null });
        throw new Error(errCrear);
      }
      handleChange('categoria', nombre);
      setMostrarNuevaCategoria(false);
      setNuevaCategoriaNombre('');
    } catch (err) {
      const msg = err instanceof Error ? err.message : '';
      setErrorNuevaCategoria(
        /permission|policy|row-level|42501/i.test(msg)
          ? 'No tienes permisos para crear categorías. Pídele a un administrador.'
          : 'No se pudo crear la categoría. Intenta de nuevo.'
      );
    } finally {
      setCreandoCategoria(false);
    }
  };
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [_isUploadingImage, _setIsUploadingImage] = useState(false);
  const [pendingImageFiles, setPendingImageFiles] = useState<File[]>([]);
  const [previewUrls, setPreviewUrls] = useState<string[]>([]);

  useEffect(() => {
    if (producto && modo === 'editar') {
      const inv = (producto as Producto).inventory_type;
      const derivedTipo: TipoOferta =
        producto.tipoOferta ||
        (producto.esSuscripcion ? 'membresia' : inv === 'unlimited' ? 'servicio' : 'producto');

      const fmtTime = (t: unknown): string => {
        if (t == null) return '';
        const s = String(t);
        return s.length >= 5 ? s.slice(0, 5) : s;
      };

      setFormData({
        nombre: producto.nombre || '',
        sku: producto.sku || '',
        barcode: (producto as any).barcode || '',
        descripcion: producto.descripcion || '',
        categoria: producto.categoria || '',
        precioCompra: producto.precioCompra?.toString() || '',
        precioVenta: producto.precioVenta?.toString() || '',
        stock: producto.stock?.toString() ?? '',
        stockMinimo: (producto.stockMinimo !== undefined && producto.stockMinimo !== null) ? producto.stockMinimo : ((producto as any).inventory_type === 'unlimited' ? 0 : 5),
        inventory_type:
          derivedTipo === 'producto'
            ? ((producto as any).inventory_type === 'unlimited' ? 'unlimited' : 'tracked')
            : 'unlimited',
        unidadMedida: producto.unidadMedida || 'Unidad',
        estado: producto.estado || 'ACTIVO',
        imagen: producto.imagen || '',
        imagenes: filterValidImageUrls((producto as any).imagenes?.length ? (producto as any).imagenes : (producto.imagen ? [producto.imagen] : [])),
        // IVA Configuration
        ivaPorcentaje: producto.ivaPorcentaje !== null && producto.ivaPorcentaje !== undefined ? producto.ivaPorcentaje : 0,
        precioConIva: producto.precioConIva || 0,
        precioSinIva: producto.precioSinIva || 0,
        valorIva: producto.valorIva || 0,
        // Catalog Configuration
        mostrarEnCatalogo: producto.mostrarEnCatalogo || false,
        mostrarPrecio: producto.mostrarPrecio !== false, // Default to true
        mostrarStock: producto.mostrarStock !== false, // Default to true
        destacado: producto.destacado || false,
        disponibleOnline: producto.disponibleOnline !== false, // Default to true
        ordenCatalogo: producto.ordenCatalogo || 0,
        tags: producto.tags || [],
        tiempoPreparacion: producto.tiempoPreparacion || '',
        ubicacion: producto.ubicacion || '',
        // Scheduling Configuration - will be loaded from bookable_items
        isBookable: false,
        requiresTimeSlot: true,
        customDurationMinutes: null,
        fulfillmentOptions: ['pickup'],
        // Subscription (from product if available)
        esSuscripcion: (producto as any).esSuscripcion || false,
        frecuenciaCobro: (producto as any).frecuenciaCobro || 'mensual',
        clasesIncluidas: (producto as any).clasesIncluidas ?? null,
        duracionDias: (producto as any).duracionDias || 30,
        autoRenovar: (producto as any).autoRenovar !== false,
        generaQrCode: (producto as any).generaQrCode !== false,
        requiereCheckin: (producto as any).requiereCheckin !== false,
        tipoOferta: derivedTipo,
        accesoLibre: producto.accesoLibre || false,
        incluyeGrupal: producto.incluyeGrupal || false,
        incluyePrivada: producto.incluyePrivada || false,
        tipoListado: (() => {
          const tl = producto.tipoListado;
          return tl === 'venta' || tl === 'arriendo' || tl === 'oferta' ? tl : '';
        })(),
        ubicacionListado: producto.ubicacionListado || '',
        areaM2: producto.areaM2 ?? null,
        atributosRows: normalizeAtributosFromDb(producto.atributos as unknown)
      });

      if (producto.id) {
        void supabase
          .from('oferta_horarios')
          .select('*')
          .eq('producto_id', producto.id)
          .eq('activo', true)
          .then(({ data, error }) => {
            if (error || !data) return;
            setHorarios(
              data.map((row: Record<string, unknown>) => ({
                tempId: String(row.id),
                tipo_slot: row.tipo_slot as HorarioSlot['tipo_slot'],
                dia_semana: String(row.dia_semana || 'lunes'),
                hora_inicio: fmtTime(row.hora_inicio) || '09:00',
                hora_fin: fmtTime(row.hora_fin) || '10:00',
                capacidad_maxima:
                  row.capacidad_maxima != null ? Number(row.capacidad_maxima) : null,
                modalidad: (row.modalidad as HorarioSlot['modalidad']) || 'presencial',
                ubicacion: String(row.ubicacion || ''),
                requiere_staff: Boolean(row.requiere_staff),
              }))
            );
          });
      } else {
        setHorarios([]);
      }

      // Load bookable_items data if editing
      if (producto && modo === 'editar' && currentEmpresa?.id) {
        loadBookableItemData(producto.id);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [producto, modo, currentEmpresa?.id]);

  // Load bookable item configuration
  const loadBookableItemData = async (productoId: string) => {
    if (!currentEmpresa?.id) return;

    try {
      const { data, error } = await supabase
        .from('bookable_items')
        .select('*')
        .eq('empresa_id', currentEmpresa.id)
        .eq('item_type', 'producto')
        .eq('item_id', productoId)
        .maybeSingle();

      if (error && error.code !== 'PGRST116') {
        console.error('Error loading bookable item:', error);
        return;
      }

      if (data) {
        setFormData(prev => ({
          ...prev,
          isBookable: data.is_bookable || false,
          requiresTimeSlot: data.requires_time_slot !== false,
          customDurationMinutes: data.custom_duration_minutes || null,
          fulfillmentOptions: data.fulfillment_options || ['pickup']
        }));
      }
    } catch (err) {
      console.error('Error loading bookable item data:', err);
    }
  };

  const handleChange = (campo: string, valor: any) => {
    setFormData(prev => {
      const newData = { ...prev, [campo]: valor };

      // Auto-calculate price with IVA when precioVenta or ivaPorcentaje changes
      if (campo === 'precioVenta' || campo === 'ivaPorcentaje') {
        const strVenta =
          campo === 'precioVenta' ? String(valor) : String(prev.precioVenta ?? '');
        const precioVentaNum = parsePrecioField(strVenta);
        const ivaPorcentaje =
          campo === 'ivaPorcentaje' ? parseFloat(valor) || 0 : prev.ivaPorcentaje || 0;

        if (!Number.isFinite(precioVentaNum)) {
          newData.precioConIva = 0;
          newData.precioSinIva = 0;
          newData.valorIva = 0;
        } else {
          const precioSinIva =
            ivaPorcentaje > 0 ? precioVentaNum / (1 + ivaPorcentaje / 100) : precioVentaNum;
          const valorIva = precioVentaNum - precioSinIva;
          newData.precioConIva = precioVentaNum;
          newData.precioSinIva = precioSinIva;
          newData.valorIva = valorIva;
        }
      }

      // Margen (solo si ambos precios son números válidos y > 0)
      if (campo === 'precioCompra' || campo === 'precioVenta') {
        const pcStr =
          campo === 'precioCompra' ? String(valor) : String(prev.precioCompra ?? '');
        const pvStr =
          campo === 'precioVenta' ? String(valor) : String(prev.precioVenta ?? '');
        const nCompra = parsePrecioField(pcStr);
        const nVenta = parsePrecioField(pvStr);
        if (Number.isFinite(nCompra) && Number.isFinite(nVenta) && nCompra > 0 && nVenta > 0) {
          (newData as { margenBruto?: number }).margenBruto = (nVenta - nCompra) / nVenta;
        }
      }

      return newData;
    });

    if (errores[campo]) {
      setErrores((prev) => ({ ...prev, [campo]: '' }));
    }
  };

  const validarFormulario = () => {
    const nuevosErrores: Record<string, string> = {};

    if (!formData.nombre?.trim()) {
      nuevosErrores.nombre = 'El nombre es obligatorio';
    }

    if (!formData.categoria?.trim()) {
      nuevosErrores.categoria = 'Selecciona una categoría para el producto';
    } else if (errorCargaCategorias) {
      nuevosErrores.categoria = 'No se pudieron cargar las categorías. Toca "Reintentar" antes de guardar.';
    } else if (!listaCategoriasLista) {
      nuevosErrores.categoria = 'Espera a que carguen las categorías';
    } else if (!esCategoriaValida(formData.categoria)) {
      nuevosErrores.categoria = categoriaInactiva(formData.categoria)
        ? `La categoría "${formData.categoria.trim()}" está inactiva. Actívala en Configuración o elige otra.`
        : `La categoría "${formData.categoria.trim()}" no está configurada en tu negocio. Elige una categoría válida.`;
    }

    if (!formData.sku?.trim() && modo !== 'crear' && formData.tipoOferta === 'producto') {
      nuevosErrores.sku = 'El SKU es obligatorio';
    }
    // En crear sin SKU: se auto-genera o se usa barcode en handleSubmit

    if (formData.precioCompra && Number(formData.precioCompra) < 0) {
      nuevosErrores.precioCompra = 'El precio de compra no puede ser negativo';
    }

    // Require price: empty, 0, or non-numeric must not save as $0
    const precioVentaStr = String(formData.precioVenta ?? '').trim();
    const precioVentaNum = parsePrecioField(precioVentaStr);
    if (!precioVentaStr) {
      nuevosErrores.precioVenta = 'El precio de venta es obligatorio';
    } else if (!Number.isFinite(precioVentaNum)) {
      nuevosErrores.precioVenta = 'El precio de venta debe ser un número válido';
    } else if (precioVentaNum <= 0) {
      nuevosErrores.precioVenta = 'El precio de venta debe ser mayor que cero';
    }

    if (formData.tipoOferta === 'producto' && formData.precioCompra && formData.precioVenta && Number(formData.precioVenta) < Number(formData.precioCompra)) {
      nuevosErrores.precioVenta = 'El precio de venta debe ser mayor al de compra';
    }

    if (formData.tipoOferta === 'producto') {
      if (formData.stock && Number(formData.stock) < 0) {
        nuevosErrores.stock = 'El stock no puede ser negativo';
      }
      if (formData.stockMinimo != null && formData.stockMinimo < 0) {
        nuevosErrores.stockMinimo = 'El stock mínimo no puede ser negativo';
      }
    }

    setErrores(nuevosErrores);
    return Object.keys(nuevosErrores).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!canEditProducto) return;
    
    if (!validarFormulario()) return;

    if (
      formData.tipoOferta === 'producto' &&
      parsePrecioField(String(formData.precioVenta ?? '')) === 0 &&
      !window.confirm('¿Publicar gratis?\n\nEste producto tiene precio $0. ¿Deseas guardarlo así?')
    ) {
      return;
    }

    setIsSubmitting(true);
    try {
      let finalBarcode = formData.barcode?.trim() || '';
      let finalSKU = formData.sku?.trim() || '';

      if (modo === 'crear' && !finalBarcode && !finalSKU) {
        const { useProductosStore } = await import('../../stores/useProductosStore');
        const { generateSKU } = useProductosStore.getState();
        if (currentEmpresa?.id) {
          finalSKU = await generateSKU(currentEmpresa.id, formData.categoria);
          finalBarcode = finalSKU;
        } else {
          finalSKU = `PRO-${Date.now().toString().slice(-4)}`;
          finalBarcode = finalSKU;
        }
      } else if (finalSKU && !finalBarcode) {
        finalBarcode = finalSKU;
      } else if (finalBarcode && !finalSKU) {
        finalSKU = finalBarcode;
      }

      const inventoryTypeFinal: 'tracked' | 'unlimited' =
        formData.tipoOferta === 'servicio' ||
        formData.tipoOferta === 'membresia' ||
        formData.tipoOferta === 'listado'
          ? 'unlimited'
          : (formData.inventory_type === 'unlimited' ? 'unlimited' : 'tracked');
      const isProductoFisico =
        formData.tipoOferta === 'producto' && inventoryTypeFinal === 'tracked';
      const precioCompra =
        formData.tipoOferta === 'membresia' || formData.tipoOferta === 'listado'
          ? 0
          : Number(formData.precioCompra) || 0;
      const precioVenta = Number(formData.precioVenta) || 0;
      const margen =
        formData.tipoOferta === 'producto' && precioCompra > 0 && precioVenta > 0
          ? (precioVenta - precioCompra) / precioVenta
          : 1.0;

      let horariosParaGuardar: HorarioSlot[] = [];
      if (formData.tipoOferta === 'servicio') {
        horariosParaGuardar = formData.isBookable
          ? horarios.filter((h) => h.tipo_slot === 'grupal')
          : [];
      } else if (formData.tipoOferta === 'membresia') {
        horariosParaGuardar = horarios.filter((h) => {
          if (h.tipo_slot === 'operacion') return formData.accesoLibre;
          if (h.tipo_slot === 'grupal') return formData.incluyeGrupal;
          if (h.tipo_slot === 'privada') return formData.incluyePrivada;
          return false;
        });
      }

      const datosCompletos: Partial<Producto> & {
        isBookable?: boolean;
        horarios?: HorarioSlot[];
        _coleccionIds?: string[];
      } = {
        ...formData,
        sku: finalSKU,
        barcode: finalBarcode || undefined,
        precioCompra,
        precioVenta,
        stock: isProductoFisico ? (Number(formData.stock) || 0) : 0,
        stockMinimo: isProductoFisico ? (formData.stockMinimo ?? 0) : 0,
        inventory_type: inventoryTypeFinal,
        margenBruto: margen,
        esSuscripcion: formData.tipoOferta === 'membresia',
        tipoOferta: formData.tipoOferta,
        tieneAgenda: formData.isBookable,
        tipoListado: formData.tipoListado || null,
        ubicacionListado: formData.ubicacionListado || null,
        areaM2: formData.areaM2,
        atributos: (() => {
          const payload = atributosRowsToDbPayload(formData.atributosRows);
          return payload.length > 0 ? (payload as unknown as Partial<Producto>['atributos']) : null;
        })(),
        accesoLibre: formData.accesoLibre,
        incluyeGrupal: formData.incluyeGrupal,
        incluyePrivada: formData.incluyePrivada,
        horarios: horariosParaGuardar,
        createdAt: modo === 'crear' ? new Date() : producto?.createdAt || new Date(),
        updatedAt: new Date(),
        imagenes: filterValidImageUrls(formData.imagenes?.length ? formData.imagenes : (formData.imagen ? [formData.imagen] : [])),
        _coleccionIds: productoColeccionIds
      };

        console.log('📤 ModalProducto: Submitting product with image:', datosCompletos.imagen);
        console.log('📤 ModalProducto: Full product data:', JSON.stringify(datosCompletos, null, 2));

      const options = modo === 'crear' && pendingImageFiles.length > 0 ? { pendingImageFiles } : undefined;
      await onGuardar(datosCompletos, options);
      
      setShowSuccess(true);
      setTimeout(() => {
        setShowSuccess(false);
        setPendingImageFiles([]);
        setPreviewUrls([]);
        onCancelar();
      }, 2000);
    } catch (error) {
      console.error('Error saving product:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const generarSKU = async () => {
    const categoriaParaSku = formData.categoria?.trim() || '';
    const prefixoCategoria = categoriaParaSku
      ? (listaCategoriasLista && esCategoriaValida(categoriaParaSku) ? categoriaParaSku : '')
      : '';

    if (currentEmpresa?.id) {
      try {
        const { useProductosStore } = await import('../../stores/useProductosStore');
        const newSKU = await useProductosStore.getState().generateSKU(currentEmpresa.id, prefixoCategoria || undefined);
        setFormData(prev => ({ ...prev, sku: newSKU }));
        if (errores.sku) {
          setErrores(prev => ({ ...prev, sku: '' }));
        }
      } catch (err) {
        const prefix = prefixoCategoria?.substring(0, 3).toUpperCase().replace(/[^A-Z]/g, '') || 'PRO';
        const timestamp = Date.now().toString().slice(-4);
        const fallbackSku = `${prefix}-${timestamp}`;
        setFormData(prev => ({ ...prev, sku: fallbackSku }));
        setErrores(prev => ({
          ...prev,
          sku: 'No se pudo generar el SKU automáticamente. Se usó un código de respaldo.',
        }));
      }
    } else {
      const prefix = prefixoCategoria?.substring(0, 3).toUpperCase().replace(/[^A-Z]/g, '') || 'PRO';
      const timestamp = Date.now().toString().slice(-4);
      setFormData(prev => ({ ...prev, sku: `${prefix}-${timestamp}` }));
    }
  };

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-20 mx-auto p-5 border w-11/12 md:w-3/4 lg:w-1/2 shadow-lg rounded-md bg-white">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center">
            <div className="p-2 bg-primary-100 rounded-lg mr-3">
              <CubeIcon className="w-6 h-6 text-primary-600" />
            </div>
            <div>
              <h3 className="text-lg font-medium text-gray-900">
                {modo === 'crear'
                  ? (
                      {
                        producto: 'Nuevo Producto',
                        servicio: 'Nuevo Servicio',
                        membresia: 'Nueva Membresía',
                        listado: 'Nuevo Listado',
                      } as const
                    )[formData.tipoOferta]
                  : readOnlyForm
                    ? 'Ver ítem'
                    : 'Editar ítem'}
              </h3>
              <p className="text-sm text-gray-500">
                {readOnlyForm
                  ? 'Solo lectura según tu rol en la empresa.'
                  : modo === 'crear'
                    ? 'Completa los datos según el tipo de oferta.'
                    : 'Modifica la información del ítem'}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {modo === 'editar' && producto?.id && onDuplicar && !readOnlyForm && (
              <button
                type="button"
                onClick={() => void onDuplicar(producto)}
                className="text-sm font-medium text-blue-600 hover:text-blue-800 px-2 py-1 rounded-md border border-blue-200"
              >
                Duplicar
              </button>
            )}
            <button
              onClick={onCancelar}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <XMarkIcon className="w-6 h-6" />
            </button>
          </div>
        </div>

        {/* Formulario */}
        <form onSubmit={handleSubmit} noValidate className="space-y-6">
          <TypeSelector
            value={formData.tipoOferta}
            disabled={readOnlyForm}
            onChange={(tipo) => {
              handleChange('tipoOferta', tipo);
              if (tipo === 'producto') {
                handleChange('inventory_type', 'tracked');
                handleChange('esSuscripcion', false);
              } else if (tipo === 'servicio') {
                handleChange('inventory_type', 'unlimited');
                handleChange('esSuscripcion', false);
                handleChange('stockMinimo', 0);
              } else if (tipo === 'membresia') {
                handleChange('inventory_type', 'unlimited');
                handleChange('esSuscripcion', true);
                handleChange('stockMinimo', 0);
              } else if (tipo === 'listado') {
                handleChange('inventory_type', 'unlimited');
                handleChange('esSuscripcion', false);
                handleChange('stockMinimo', 0);
              }
            }}
          />

          {/* Información básica */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label htmlFor="nombre" className="block text-sm font-medium text-gray-700 mb-1">
                Nombre del producto *
              </label>
              <input
                type="text"
                id="nombre"
                autoComplete="off"
                value={formData.nombre}
                onChange={(e) => handleChange('nombre', e.target.value)}
                className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500 ${
                  errores.nombre ? 'border-red-300' : 'border-gray-300'
                }`}
                placeholder="Ej: Laptop HP Pavilion"
                required
                disabled={readOnlyForm}
              />
              {errores.nombre && (
                <p className="mt-1 text-sm text-red-600">{errores.nombre}</p>
              )}
            </div>

            {formData.tipoOferta === 'producto' && (
              <>
                <div>
                  <label htmlFor="sku" className="block text-sm font-medium text-gray-700 mb-1">
                    SKU *
                  </label>
                  <div className="flex space-x-2">
                    <input
                      type="text"
                      id="sku"
                      autoComplete="off"
                      value={formData.sku}
                      onChange={(e) => handleChange('sku', e.target.value)}
                      className={`flex-1 px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500 ${
                        errores.sku ? 'border-red-300' : 'border-gray-300'
                      }`}
                      placeholder="Ej: LAP-001"
                      required={modo === 'editar'}
                      disabled={readOnlyForm}
                    />
                    <button
                      type="button"
                      onClick={generarSKU}
                      disabled={readOnlyForm}
                      className="px-3 py-2 text-sm border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-1 focus:ring-primary-500 disabled:opacity-50"
                    >
                      Auto
                    </button>
                  </div>
                  {errores.sku && (
                    <p className="mt-1 text-sm text-red-600">{errores.sku}</p>
                  )}
                </div>

                <div>
                  <label htmlFor="barcode" className="block text-sm font-medium text-gray-700 mb-1">
                    Código de barras
                  </label>
                  <input
                    type="text"
                    id="barcode"
                    value={formData.barcode}
                    onChange={(e) => handleChange('barcode', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
                    placeholder="Escanea o ingresa EAN-13, UPC, CODE-128"
                    disabled={readOnlyForm}
                  />
                  <p className="mt-1 text-xs text-gray-500">
                    EAN-13, UPC, CODE-128 o código interno
                  </p>
                </div>

                {modo === 'crear' && !formData.barcode && !formData.sku && (
                  <div className="col-span-full bg-blue-50 border border-blue-200 rounded-lg p-3">
                    <p className="text-sm text-blue-800">
                      💡 <strong>Código de barras automático:</strong> Si no ingresas un código, Clientelo generará uno que podrás imprimir como etiqueta.
                    </p>
                  </div>
                )}
              </>
            )}
          </div>

          <div>
            <label htmlFor="descripcion" className="block text-sm font-medium text-gray-700 mb-1">
              Descripción
            </label>
            <textarea
              id="descripcion"
              value={formData.descripcion}
              onChange={(e) => handleChange('descripcion', e.target.value)}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
              placeholder="Describe las características del producto..."
              disabled={readOnlyForm}
            />
          </div>

          <AtributosEditor
            empresaId={currentEmpresa?.id}
            value={formData.atributosRows}
            onChange={(rows) => handleChange('atributosRows', rows)}
            disabled={readOnlyForm}
            listadoPresets={formData.tipoOferta === 'listado'}
          />

          {/* Imágenes del Producto: galería (editar) o subida múltiple (crear) */}
          <div>
            {producto?.id ? (
              readOnlyForm ? (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Imágenes</label>
                  <div className="flex flex-wrap gap-2">
                    {filterValidImageUrls(formData.imagenes?.length ? formData.imagenes : (formData.imagen ? [formData.imagen] : [])).map((url) => (
                      <img key={url} src={url} alt="" className="w-24 h-24 object-cover rounded-lg border border-gray-200" />
                    ))}
                  </div>
                </div>
              ) : (
              <ImageGallery
                productoId={producto.id}
                images={filterValidImageUrls(formData.imagenes?.length ? formData.imagenes : (formData.imagen ? [formData.imagen] : []))}
                onImagesChange={(newImages) => {
                  setFormData(prev => ({
                    ...prev,
                    imagenes: newImages,
                    imagen: newImages[0] || prev.imagen
                  }));
                }}
              />
              )
            ) : (
              <>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Imágenes del Producto
                </label>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-3">
                  {previewUrls.map((url, index) => (
                    <div key={url} className="relative aspect-square rounded-lg border border-gray-200 overflow-hidden">
                      <img src={url} alt="" className="w-full h-full object-cover" />
                      {!readOnlyForm && (
                      <button
                        type="button"
                        onClick={() => {
                          setPendingImageFiles(prev => prev.filter((_, i) => i !== index));
                          setPreviewUrls(prev => {
                            const next = prev.filter((_, i) => i !== index);
                            URL.revokeObjectURL(url);
                            return next;
                          });
                        }}
                        className="absolute top-1 right-1 p-1 bg-red-600 text-white rounded hover:bg-red-700"
                        title="Quitar"
                      >
                        <Trash2 className="h-3 w-3" />
                      </button>
                      )}
                    </div>
                  ))}
                </div>
                {!readOnlyForm && (
                <label className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 cursor-pointer text-sm font-medium">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
                  </svg>
                  Seleccionar imágenes
                  <input
                    type="file"
                    accept="image/*"
                    multiple
                    onChange={(e) => {
                      const files = e.target.files;
                      if (!files?.length) return;
                      const fileArray = Array.from(files);
                      setPendingImageFiles(prev => [...prev, ...fileArray]);
                      const urls = fileArray.map(f => URL.createObjectURL(f));
                      setPreviewUrls(prev => [...prev, ...urls]);
                      e.target.value = '';
                    }}
                    className="hidden"
                  />
                </label>
                )}
                <p className="text-xs text-gray-500 mt-2">
                  Las imágenes se subirán al guardar el producto. Puedes agregar varias.
                </p>
              </>
            )}
          </div>

          {/* Categoría y estado */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <div className="flex items-center justify-between mb-1">
                <label htmlFor="categoria" className="block text-sm font-medium text-gray-700">
                  Categoría
                </label>
                {!readOnlyForm && (
                <button
                  type="button"
                  onClick={(e) => {
                    e.preventDefault();
                    setMostrarNuevaCategoria(true);
                    setErrorNuevaCategoria('');
                  }}
                  className="text-xs text-blue-600 hover:text-blue-800 hover:underline"
                  title="Crear una categoría sin salir del formulario"
                >
                  + Nueva Categoría
                </button>
                )}
              </div>
              <select
                id="categoria"
                value={formData.categoria}
                onChange={(e) => handleChange('categoria', e.target.value)}
                disabled={categoriasLoading || readOnlyForm}
                required
                className={`w-full px-3 py-2 border ${errores.categoria ? 'border-red-300' : 'border-gray-300'} rounded-md shadow-sm focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500 disabled:bg-gray-100 disabled:cursor-not-allowed`}
              >
                <option value="">{categoriasLoading ? 'Cargando categorías...' : 'Seleccionar categoría...'}</option>
                {formData.categoria && !hayOpcionExacta(formData.categoria) && (
                  <option value={formData.categoria} disabled>
                    {formData.categoria}
                    {listaCategoriasLista
                      ? esCategoriaValida(formData.categoria)
                        ? categoriaInactiva(formData.categoria) ? ' (inactiva)' : ''
                        : ' (no válida)'
                      : ''}
                  </option>
                )}
                {categoriasActivas.map((categoria) => (
                  <option key={categoria.id} value={categoria.nombre}>
                    {categoria.icono ? `${categoria.icono} ` : ''}{categoria.nombre}
                  </option>
                ))}
              </select>
              {errores.categoria ? (
                <p className="mt-1 text-sm text-red-600">{errores.categoria}</p>
              ) : (
                listaCategoriasLista &&
                formData.categoria &&
                !esCategoriaValida(formData.categoria) && (
                  <p className="mt-1 text-sm text-red-600">
                    La categoría "{formData.categoria}" no está configurada en tu negocio. Elige una categoría válida.
                  </p>
                )
              )}
              {errorCargaCategorias && (
                <div className="mt-2 rounded-md border border-red-200 bg-red-50 p-2 text-sm text-red-700">
                  {errorCargaCategorias}{' '}
                  <button
                    type="button"
                    onClick={() => void cargarCategorias()}
                    className="font-medium underline hover:text-red-900"
                  >
                    Reintentar
                  </button>
                </div>
              )}
              {listaCategoriasLista && categoriasActivas.length === 0 && !mostrarNuevaCategoria && (
                <div className="mt-2 rounded-md border border-amber-200 bg-amber-50 p-2 text-sm text-amber-800">
                  Tu negocio aún no tiene categorías.{' '}
                  {!readOnlyForm && (
                    <button
                      type="button"
                      onClick={() => setMostrarNuevaCategoria(true)}
                      className="font-medium underline hover:text-amber-900"
                    >
                      Crea la primera aquí
                    </button>
                  )}
                </div>
              )}
              {mostrarNuevaCategoria && !readOnlyForm && listaCategoriasLista && (
                <div className="mt-2 rounded-md border border-gray-200 bg-gray-50 p-2">
                  <label htmlFor="nuevaCategoria" className="block text-xs font-medium text-gray-700 mb-1">
                    Nueva categoría
                  </label>
                  <div className="flex gap-2">
                    <input
                      id="nuevaCategoria"
                      type="text"
                      autoComplete="off"
                      value={nuevaCategoriaNombre}
                      onChange={(e) => setNuevaCategoriaNombre(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          void handleCrearCategoriaInline();
                        }
                      }}
                      placeholder="Ej: Velas"
                      className="flex-1 px-2 py-1 border border-gray-300 rounded-md text-sm"
                      disabled={creandoCategoria}
                    />
                    <button
                      type="button"
                      onClick={() => void handleCrearCategoriaInline()}
                      disabled={creandoCategoria}
                      className="px-3 py-1 rounded-md bg-primary-600 text-white text-sm disabled:opacity-50"
                    >
                      {creandoCategoria ? 'Creando…' : 'Crear'}
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setMostrarNuevaCategoria(false);
                        setNuevaCategoriaNombre('');
                        setErrorNuevaCategoria('');
                      }}
                      className="px-2 py-1 text-sm text-gray-600 hover:text-gray-800"
                    >
                      Cancelar
                    </button>
                  </div>
                  {errorNuevaCategoria && (
                    <p className="mt-1 text-xs text-red-600">{errorNuevaCategoria}</p>
                  )}
                </div>
              )}
              {!readOnlyForm && (
              <p className="mt-1 text-xs text-gray-500">
                <button
                  type="button"
                  onClick={(e) => {
                    e.preventDefault();
                    window.open('/configuracion?tab=categorias', '_blank', 'noopener');
                  }}
                  className="text-blue-600 hover:text-blue-800 hover:underline"
                >
                  Gestionar todas las categorías (nueva pestaña) ↗
                </button>
              </p>
              )}
            </div>

            <div>
              <label htmlFor="estado" className="block text-sm font-medium text-gray-700 mb-1">
                Estado
              </label>
              <select
                id="estado"
                value={formData.estado}
                onChange={(e) => handleChange('estado', e.target.value)}
                disabled={readOnlyForm}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
              >
                <option value="ACTIVO">Activo</option>
                <option value="DESCONTINUADO">Descontinuado</option>
                <option value="AGOTADO">Agotado</option>
              </select>
            </div>
          </div>

          {/* Colecciones (catálogo / campañas) */}
          <div className="mt-2">
            <label className="mb-1 block text-sm font-medium text-gray-700">
              Colecciones <span className="font-normal text-gray-400">(opcional)</span>
            </label>
            <p className="mb-3 text-xs text-gray-500">
              Agrupa ítems para el catálogo público: temporadas, promociones o temas. Un ítem puede estar en varias colecciones.
            </p>
            {coleccionesLoading ? (
              <p className="text-sm text-gray-400">Cargando colecciones…</p>
            ) : colecciones.length === 0 ? (
              <p className="text-sm italic text-gray-400">
                No hay colecciones aún.{' '}
                <button
                  type="button"
                  onClick={() => navigate('/colecciones')}
                  className="text-blue-600 hover:text-blue-800"
                >
                  Ir a Colecciones
                </button>
              </p>
            ) : (
              <div className="flex flex-wrap gap-2">
                {colecciones.map((col) => {
                  const isIn = productoColeccionIds.includes(col.id);
                  return (
                    <button
                      key={col.id}
                      type="button"
                      disabled={readOnlyForm}
                      onClick={() => {
                        if (readOnlyForm) return;
                        setProductoColeccionIds((prev) =>
                          prev.includes(col.id) ? prev.filter((x) => x !== col.id) : [...prev, col.id]
                        );
                      }}
                      className={`inline-flex items-center gap-1.5 rounded-full border-2 px-3 py-1.5 text-sm font-medium transition-all ${
                        isIn ? 'opacity-100' : 'opacity-60 hover:opacity-100'
                      }`}
                      style={
                        isIn
                          ? {
                              borderColor: col.color,
                              color: '#fff',
                              backgroundColor: col.color,
                            }
                          : {
                              borderColor: col.color,
                              color: col.color,
                              backgroundColor: `${col.color}1a`,
                            }
                      }
                    >
                      {col.icono && <span>{col.icono}</span>}
                      {col.nombre}
                      {isIn && <span className="ml-0.5">✓</span>}
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          {/* Precios */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {(formData.tipoOferta === 'producto' || formData.tipoOferta === 'servicio') && canSeeCosto && (
              <div>
                <label htmlFor="precioCompra" className="block text-sm font-medium text-gray-700 mb-1">
                  Precio de compra (costo)
                </label>
                <div className="relative">
                  <span className="absolute left-3 top-2 text-gray-500">$</span>
                  <input
                    type="text"
                    inputMode="decimal"
                    id="precioCompra"
                    value={formData.precioCompra}
                    onChange={(e) => handleChange('precioCompra', e.target.value)}
                    className={`w-full pl-8 pr-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500 ${
                      errores.precioCompra ? 'border-red-300' : 'border-gray-300'
                    }`}
                    placeholder="0"
                    disabled={readOnlyForm}
                  />
                </div>
                {errores.precioCompra && (
                  <p className="mt-1 text-sm text-red-600">{errores.precioCompra}</p>
                )}
              </div>
            )}
            {formData.tipoOferta === 'servicio' && (
              <div className="rounded-md bg-blue-50 border border-blue-200 p-3">
                <p className="text-sm text-blue-700">
                  💼 <strong>Servicio:</strong> El costo es opcional; si no aplica, déjalo en 0.
                </p>
              </div>
            )}

            <div>
              <label htmlFor="precioVenta" className="block text-sm font-medium text-gray-700 mb-1">
                Precio de venta (incluye IVA)
              </label>
              <div className="relative">
                <span className="absolute left-3 top-2 text-gray-500">$</span>
                <input
                  type="text"
                  inputMode="decimal"
                  id="precioVenta"
                  value={formData.precioVenta}
                  onChange={(e) => handleChange('precioVenta', e.target.value)}
                  className={`w-full pl-8 pr-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500 ${
                    errores.precioVenta ? 'border-red-300' : 'border-gray-300'
                  }`}
                  placeholder="0"
                  disabled={precioVentaLocked}
                />
              </div>
              <p className="mt-1 text-xs text-gray-500">
                Este es el precio final que verán los clientes (incluye IVA)
              </p>
              {canEditProducto && !canEditPrecio && (
                <p className="mt-1 text-xs text-amber-700">Tu rol no puede modificar el precio de venta.</p>
              )}
              {errores.precioVenta && (
                <p className="mt-1 text-sm text-red-600">{errores.precioVenta}</p>
              )}
            </div>
          </div>

          {/* IVA Configuration */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label htmlFor="ivaPorcentaje" className="block text-sm font-medium text-gray-700 mb-1">
                Porcentaje de IVA incluido
              </label>
              <div className="relative">
                <input
                  type="number"
                  id="ivaPorcentaje"
                  value={formData.ivaPorcentaje}
                  onChange={(e) => handleChange('ivaPorcentaje', parseFloat(e.target.value) || 0)}
                  className="w-full px-3 py-2 pr-8 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
                  placeholder="19"
                  min="0"
                  max="100"
                  step="0.01"
                  disabled={readOnlyForm}
                />
                <span className="absolute right-3 top-2 text-gray-500">%</span>
              </div>
              <p className="mt-1 text-xs text-gray-500">
                El IVA se incluye en el precio de venta (ej: 19% para Colombia)
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Precio final (calculado)
              </label>
              <div className="relative">
                <span className="absolute left-3 top-2 text-gray-500">$</span>
                <input
                  type="number"
                  value={formData.precioConIva.toFixed(2)}
                  readOnly
                  className="w-full pl-8 pr-3 py-2 border border-gray-300 rounded-md shadow-sm bg-gray-50 text-gray-600"
                  placeholder="0.00"
                />
              </div>
              <p className="mt-1 text-xs text-gray-500">
                Igual al precio de venta (incluye IVA)
              </p>
            </div>
          </div>

          {/* Price breakdown and margin */}
          {(formData.precioVenta || formData.ivaPorcentaje) && (
            <div className="bg-blue-50 border border-blue-200 p-4 rounded-md">
              <h4 className="text-sm font-medium text-blue-900 mb-3">Desglose de Precios</h4>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-blue-700">Precio final (incluye IVA):</span>
                  <span className="font-medium text-blue-900">${Number(formData.precioVenta || 0).toLocaleString()}</span>
                </div>
                {formData.ivaPorcentaje && Number(formData.ivaPorcentaje) > 0 && (
                  <>
                    <div className="flex justify-between">
                      <span className="text-blue-700">Precio sin IVA:</span>
                      <span className="text-blue-800">${(Number(formData.precioSinIva || 0)).toLocaleString()}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-blue-700">IVA ({formData.ivaPorcentaje}%):</span>
                      <span className="text-blue-800">${(Number(formData.valorIva || 0)).toLocaleString()}</span>
                    </div>
                  </>
                )}
              </div>
            </div>
          )}

          {/* Margen calculado — solo para productos con inventario */}
          {canSeeCosto && formData.tipoOferta === 'producto' && formData.precioCompra && formData.precioVenta && (
            <div className="bg-gray-50 p-3 rounded-md">
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Margen bruto calculado:</span>
                <span className={`font-medium ${
                  (() => {
                    const precioCompra = Number(formData.precioCompra) || 0;
                    const precioVenta = Number(formData.precioVenta) || 0;
                    const margen = precioCompra > 0 && precioVenta > 0 ? (precioVenta - precioCompra) / precioVenta : 0;
                    return margen >= 0.5 ? 'text-green-600' : margen >= 0.3 ? 'text-yellow-600' : 'text-red-600';
                  })()
                }`}>
                  {(() => {
                    const precioCompra = Number(formData.precioCompra) || 0;
                    const precioVenta = Number(formData.precioVenta) || 0;
                    const margen = precioCompra > 0 && precioVenta > 0 ? (precioVenta - precioCompra) / precioVenta : 0;
                    return (margen * 100).toFixed(1) + '%';
                  })()}
                </span>
              </div>
            </div>
          )}

          {/* Stock — solo producto */}
          {formData.tipoOferta === 'producto' && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="md:col-span-3 rounded-md border border-gray-200 bg-gray-50 p-3">
              <label className="inline-flex items-center gap-2 text-sm font-medium text-gray-700 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.inventory_type === 'unlimited'}
                  onChange={(e) => {
                    const unlimited = e.target.checked;
                    handleChange('inventory_type', unlimited ? 'unlimited' : 'tracked');
                    if (unlimited) {
                      handleChange('stockMinimo', 0);
                    } else if ((formData.stock || '').trim() === '') {
                      handleChange('stock', '0');
                    }
                  }}
                  className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                  disabled={readOnlyForm}
                />
                Stock ilimitado (sin límite)
              </label>
              <p className="mt-1 text-xs text-gray-500">
                Actívalo para no descontar inventario ni generar alertas de stock bajo.
              </p>
            </div>

            {formData.inventory_type !== 'unlimited' ? (
              <>
            <div>
              <label htmlFor="stock" className="block text-sm font-medium text-gray-700 mb-1">
                Stock actual
              </label>
              <div className="flex items-center gap-1">
                <button
                  type="button"
                  disabled={readOnlyForm}
                  onClick={() => {
                    const cur = parseInt(String(formData.stock), 10);
                    const n = Number.isNaN(cur) ? 0 : cur;
                    handleChange('stock', String(Math.max(0, n - 1)));
                  }}
                  className="shrink-0 w-9 h-10 flex items-center justify-center border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 disabled:opacity-50 text-lg leading-none"
                  aria-label="Reducir stock"
                >
                  −
                </button>
                <input
                  type="text"
                  id="stock"
                  inputMode="numeric"
                  autoComplete="off"
                  value={formData.stock}
                  onChange={(e) => {
                    const v = e.target.value;
                    if (v === '' || /^\d+$/.test(v)) handleChange('stock', v);
                  }}
                  onBlur={() => {
                    if (formData.stock === '') handleChange('stock', '0');
                  }}
                  className={`min-w-0 flex-1 px-3 py-2 border rounded-md shadow-sm text-center focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500 ${
                    errores.stock ? 'border-red-300' : 'border-gray-300'
                  }`}
                  placeholder="0"
                  disabled={readOnlyForm}
                />
                <button
                  type="button"
                  disabled={readOnlyForm}
                  onClick={() => {
                    const cur = parseInt(String(formData.stock), 10);
                    const n = Number.isNaN(cur) ? 0 : cur;
                    handleChange('stock', String(n + 1));
                  }}
                  className="shrink-0 w-9 h-10 flex items-center justify-center border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 disabled:opacity-50 text-lg leading-none"
                  aria-label="Aumentar stock"
                >
                  +
                </button>
              </div>
              <p className="mt-1 text-xs text-gray-500">Puedes borrar y escribir el stock; «−» no baja de 0.</p>
              {errores.stock && (
                <p className="mt-1 text-sm text-red-600">{errores.stock}</p>
              )}
            </div>

            <div>
              <label htmlFor="stockMinimo" className="block text-sm font-medium text-gray-700 mb-1">
                Stock mínimo
              </label>
              <input
                type="number"
                id="stockMinimo"
                value={formData.stockMinimo}
                onChange={(e) => handleChange('stockMinimo', e.target.value === '' ? 0 : parseInt(e.target.value, 10))}
                className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500 ${
                  errores.stockMinimo ? 'border-red-300' : 'border-gray-300'
                }`}
                placeholder="0"
                min="0"
                disabled={readOnlyForm}
              />
              {errores.stockMinimo && (
                <p className="mt-1 text-sm text-red-600">{errores.stockMinimo}</p>
              )}
              <p className="mt-1 text-xs text-gray-500">
                Dejar en 0 para no recibir alertas de stock bajo.
              </p>
            </div>

            <div>
              <label htmlFor="unidadMedida" className="block text-sm font-medium text-gray-700 mb-1">
                Unidad de medida
              </label>
              <select
                id="unidadMedida"
                value={formData.unidadMedida}
                onChange={(e) => handleChange('unidadMedida', e.target.value)}
                disabled={readOnlyForm}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
              >
                <option value="Unidad">Unidad</option>
                <option value="Kilogramo">Kilogramo</option>
                <option value="Litro">Litro</option>
                <option value="Metro">Metro</option>
                <option value="Hora">Hora</option>
                <option value="Servicio">Servicio</option>
              </select>
            </div>
              </>
            ) : (
              <div className="md:col-span-3 rounded-md border border-blue-100 bg-blue-50 p-3">
                <p className="text-sm text-blue-800">
                  Este producto se manejará con stock ilimitado. Siempre se mostrará como disponible.
                </p>
              </div>
            )}
          </div>
          )}
          {formData.tipoOferta !== 'producto' && (
            <div className="rounded-md bg-gray-50 border border-gray-200 p-3">
              <p className="text-sm text-gray-600">
                Este tipo de oferta no usa stock contable; se considera siempre disponible salvo que indiques lo contrario en catálogo.
              </p>
            </div>
          )}

          {/* Catalog + suscripción (solo lectura si el rol no edita productos) */}
          <fieldset disabled={readOnlyForm} className="min-w-0 border-0 p-0 m-0">
          <div className="border-t pt-6 mt-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">🌐 Configuración del Catálogo Online</h3>
            
            {/* Basic Catalog Settings */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="mostrarEnCatalogo"
                  checked={formData.mostrarEnCatalogo}
                  onChange={(e) => handleChange('mostrarEnCatalogo', e.target.checked)}
                  className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                />
                <label htmlFor="mostrarEnCatalogo" className="ml-2 block text-sm text-gray-700">
                  Mostrar en catálogo público
                </label>
              </div>

              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="disponibleOnline"
                  checked={formData.disponibleOnline}
                  onChange={(e) => handleChange('disponibleOnline', e.target.checked)}
                  className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                />
                <label htmlFor="disponibleOnline" className="ml-2 block text-sm text-gray-700">
                  Disponible para compra online
                </label>
              </div>

              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="mostrarPrecio"
                  checked={formData.mostrarPrecio}
                  onChange={(e) => handleChange('mostrarPrecio', e.target.checked)}
                  className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                />
                <label htmlFor="mostrarPrecio" className="ml-2 block text-sm text-gray-700">
                  Mostrar precio en catálogo
                </label>
              </div>

              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="mostrarStock"
                  checked={formData.mostrarStock}
                  onChange={(e) => handleChange('mostrarStock', e.target.checked)}
                  className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                />
                <label htmlFor="mostrarStock" className="ml-2 block text-sm text-gray-700">
                  Mostrar stock disponible
                </label>
              </div>
            </div>

            {/* Advanced Catalog Settings */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="destacado"
                  checked={formData.destacado}
                  onChange={(e) => handleChange('destacado', e.target.checked)}
                  className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                />
                <label htmlFor="destacado" className="ml-2 block text-sm text-gray-700">
                  Producto destacado
                </label>
              </div>

              <div>
                <label htmlFor="ordenCatalogo" className="block text-sm font-medium text-gray-700 mb-1">
                  Orden en catálogo
                </label>
                <input
                  type="number"
                  id="ordenCatalogo"
                  value={formData.ordenCatalogo}
                  onChange={(e) => handleChange('ordenCatalogo', parseInt(e.target.value) || 0)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
                  placeholder="0"
                  min="0"
                />
                <p className="text-xs text-gray-500 mt-1">Número menor = aparece primero</p>
              </div>
            </div>

            {/* Additional Catalog Information */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              <div>
                <label htmlFor="tiempoPreparacion" className="block text-sm font-medium text-gray-700 mb-1">
                  Tiempo de preparación
                </label>
                <input
                  type="text"
                  id="tiempoPreparacion"
                  value={formData.tiempoPreparacion}
                  onChange={(e) => handleChange('tiempoPreparacion', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
                  placeholder="24 horas, Inmediato"
                />
              </div>

              <div>
                <label htmlFor="ubicacion" className="block text-sm font-medium text-gray-700 mb-1">
                  Ubicación
                </label>
                <select
                  id="ubicacion"
                  value={formData.ubicacion}
                  onChange={(e) => handleChange('ubicacion', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
                  disabled={ubicacionesLoading}
                >
                  <option value="">
                    {ubicacionesLoading ? 'Cargando ubicaciones...' : 'Sin ubicación'}
                  </option>
                  {formData.ubicacion && !ubicaciones.some((u) => u.activa && ([u.nombre, u.direccion, u.ciudad].filter(Boolean).join(' – ') || u.nombre) === formData.ubicacion) && (
                    <option value={formData.ubicacion}>{formData.ubicacion}</option>
                  )}
                  {currentEmpresa && ubicaciones.length === 0 && !ubicacionesLoading && (
                    <option value={[currentEmpresa.direccion, currentEmpresa.ciudad].filter(Boolean).join(', ') || 'Principal'}>
                      Principal {[currentEmpresa.direccion, currentEmpresa.ciudad].filter(Boolean).length ? `(${[currentEmpresa.direccion, currentEmpresa.ciudad].filter(Boolean).join(', ')})` : ''}
                    </option>
                  )}
                  {ubicaciones.filter((u) => u.activa).map((u) => {
                    const label = [u.nombre, u.direccion, u.ciudad].filter(Boolean).join(' – ') || u.nombre;
                    const value = label;
                    return (
                      <option key={u.id} value={value}>
                        {label}
                      </option>
                    );
                  })}
                </select>
                <p className="mt-1 text-xs text-gray-500">
                  <button
                    type="button"
                    onClick={(e) => { e.preventDefault(); navigate('/configuracion?tab=ubicaciones'); }}
                    className="text-blue-600 hover:text-blue-800 underline"
                  >
                    Gestionar ubicaciones
                  </button>
                </p>
              </div>
            </div>

            {/* Tags */}
            <div>
              <label htmlFor="tags" className="block text-sm font-medium text-gray-700 mb-1">
                Etiquetas (tags)
              </label>
              <input
                type="text"
                id="tags"
                value={formData.tags?.join(', ') || ''}
                onChange={(e) => {
                  const tags = e.target.value.split(',').map(tag => tag.trim()).filter(tag => tag);
                  handleChange('tags', tags);
                }}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
                placeholder="ropa, mujer, verano (separados por comas)"
              />
              <p className="text-xs text-gray-500 mt-1">Etiquetas para mejorar la búsqueda en el catálogo</p>
            </div>
          </div>

          {/* Membresía — suscripción (sin checkbox: implícito por tipo) */}
          {formData.tipoOferta === 'membresia' && (
            <div className="border-t pt-6 mt-6">
              <h3 className="text-sm font-medium text-gray-800 mb-3">Suscripción / cobro</h3>
              <div className="space-y-4 pl-1 sm:pl-6 border-l-2 border-blue-200 bg-blue-50 p-4 rounded-r">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Frecuencia de cobro
                    </label>
                    <select
                      value={formData.frecuenciaCobro || 'mensual'}
                      onChange={(e) => handleChange('frecuenciaCobro', e.target.value)}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2"
                    >
                      <option value="semanal">Semanal</option>
                      <option value="mensual">Mensual</option>
                      <option value="trimestral">Trimestral</option>
                      <option value="anual">Anual</option>
                      <option value="por_clase">Por clase</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Clases/Visitas incluidas
                    </label>
                    <input
                      type="number"
                      placeholder="Deja vacío para ilimitado"
                      value={formData.clasesIncluidas ?? ''}
                      onChange={(e) =>
                        handleChange('clasesIncluidas', e.target.value ? parseInt(e.target.value, 10) : null)
                      }
                      className="w-full border border-gray-300 rounded-lg px-3 py-2"
                    />
                    <p className="text-xs text-gray-500 mt-1">Vacío = Ilimitado</p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Duración del período (días)
                    </label>
                    <input
                      type="number"
                      value={formData.duracionDias || 30}
                      onChange={(e) => handleChange('duracionDias', parseInt(e.target.value, 10) || 30)}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={formData.autoRenovar ?? true}
                      onChange={(e) => handleChange('autoRenovar', e.target.checked)}
                      className="rounded"
                    />
                    <span className="text-sm">Renovación automática</span>
                  </label>

                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={formData.generaQrCode ?? true}
                      onChange={(e) => handleChange('generaQrCode', e.target.checked)}
                      className="rounded"
                    />
                    <span className="text-sm">Generar código QR para check-in</span>
                  </label>

                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={formData.requiereCheckin ?? true}
                      onChange={(e) => handleChange('requiereCheckin', e.target.checked)}
                      className="rounded"
                    />
                    <span className="text-sm">Requiere registro de asistencia</span>
                  </label>
                </div>
              </div>
            </div>
          )}
          </fieldset>

          {/* Agendamiento — solo servicios */}
          {formData.tipoOferta === 'servicio' && (
            <fieldset disabled={readOnlyForm} className="border-t pt-6 mt-6 min-w-0 border-x-0 border-b-0 border-gray-200">
              <div className="flex items-center space-x-2 mb-4">
                <CalendarIcon className="h-5 w-5 text-primary-600" />
                <h3 className="text-lg font-medium text-gray-900">📅 Configuración de Agendamiento</h3>
              </div>

              <div className="space-y-4">
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="isBookable"
                    checked={formData.isBookable}
                    onChange={(e) => handleChange('isBookable', e.target.checked)}
                    className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                  />
                  <label htmlFor="isBookable" className="ml-2 block text-sm text-gray-700">
                    ¿Requiere agendamiento?
                  </label>
                </div>
                <p className="text-xs text-gray-500 ml-6">
                  Si está activado, los clientes podrán seleccionar fecha y hora al pedir este servicio
                </p>

                {formData.isBookable && (
                  <div className="ml-6 space-y-4 border-l-2 border-gray-200 pl-4">
                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        id="requiresTimeSlot"
                        checked={formData.requiresTimeSlot}
                        onChange={(e) => handleChange('requiresTimeSlot', e.target.checked)}
                        className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                      />
                      <label htmlFor="requiresTimeSlot" className="ml-2 block text-sm text-gray-700">
                        Requiere selección de fecha y hora
                      </label>
                    </div>

                    <div>
                      <label htmlFor="customDurationMinutes" className="block text-sm font-medium text-gray-700 mb-1">
                        Duración personalizada (minutos)
                      </label>
                      <input
                        type="number"
                        id="customDurationMinutes"
                        value={formData.customDurationMinutes || ''}
                        onChange={(e) =>
                          handleChange('customDurationMinutes', e.target.value ? parseInt(e.target.value, 10) : null)
                        }
                        className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
                        placeholder="Dejar vacío para usar duración predeterminada"
                        min="15"
                        step="15"
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        Duración específica (15, 30, 45, 60…). Si está vacío, se usa la duración del Calendario.
                      </p>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Opciones de cumplimiento
                      </label>
                      <div className="space-y-2">
                        {[
                          { value: 'pickup', label: 'Recoger en tienda' },
                          { value: 'delivery', label: 'Entrega a domicilio' },
                          { value: 'on-site', label: 'Servicio en sitio' },
                        ].map((option) => (
                          <div key={option.value} className="flex items-center">
                            <input
                              type="checkbox"
                              id={`fulfillment_${option.value}`}
                              checked={formData.fulfillmentOptions.includes(option.value)}
                              onChange={(e) => {
                                const current = formData.fulfillmentOptions;
                                if (e.target.checked) {
                                  handleChange('fulfillmentOptions', [...current, option.value]);
                                } else {
                                  handleChange(
                                    'fulfillmentOptions',
                                    current.filter((v) => v !== option.value)
                                  );
                                }
                              }}
                              className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                            />
                            <label htmlFor={`fulfillment_${option.value}`} className="ml-2 block text-sm text-gray-700">
                              {option.label}
                            </label>
                          </div>
                        ))}
                      </div>
                      <p className="text-xs text-gray-500 mt-2">
                        Selecciona las opciones disponibles para este servicio
                      </p>
                    </div>

                    <div>
                      <p className="text-sm font-medium text-gray-800 mb-2">Horarios de oferta (cupos)</p>
                      <p className="text-xs text-gray-500 mb-2">
                        Define franjas por día; por ejemplo lunes capacidad 10 y martes 30.
                      </p>
                      <HorariosBuilder
                        tipoSlot="grupal"
                        horarios={horarios.filter((h) => h.tipo_slot === 'grupal')}
                        showCapacity
                        onChange={(slots) =>
                          setHorarios((prev) => [...prev.filter((h) => h.tipo_slot !== 'grupal'), ...slots])
                        }
                      />
                    </div>
                  </div>
                )}
              </div>
            </fieldset>
          )}

          {/* Membresía — tipos de acceso y horarios */}
          {formData.tipoOferta === 'membresia' && (
            <fieldset
              disabled={readOnlyForm}
              className="border-t pt-6 mt-6 min-w-0 border-x-0 border-b-0 border-gray-200"
            >
              <h3 className="text-lg font-medium text-gray-900 mb-1">🎯 Tipo de acceso incluido</h3>
              <p className="text-sm text-gray-500 mb-4">
                Selecciona qué incluye esta membresía. Puedes combinar varios tipos.
              </p>

              <div className="mb-4">
                <label className="flex items-center gap-3 mb-2">
                  <input
                    type="checkbox"
                    checked={formData.accesoLibre}
                    onChange={(e) => handleChange('accesoLibre', e.target.checked)}
                    className="h-4 w-4 rounded border-gray-300"
                  />
                  <div>
                    <span className="text-sm font-medium text-gray-700">🏃 Acceso libre</span>
                    <p className="text-xs text-gray-400">El miembro puede asistir cuando quiera, sin reserva</p>
                  </div>
                </label>
                {formData.accesoLibre && (
                  <div className="ml-7 pl-3 border-l-2 border-gray-200">
                    <p className="text-xs text-gray-500 mb-2">
                      Horario de operación (opcional — para mostrar en catálogo)
                    </p>
                    <HorariosBuilder
                      tipoSlot="operacion"
                      horarios={horarios.filter((h) => h.tipo_slot === 'operacion')}
                      showCapacity={false}
                      onChange={(slots) =>
                        setHorarios((prev) => [...prev.filter((h) => h.tipo_slot !== 'operacion'), ...slots])
                      }
                    />
                  </div>
                )}
              </div>

              <div className="mb-4">
                <label className="flex items-center gap-3 mb-2">
                  <input
                    type="checkbox"
                    checked={formData.incluyeGrupal}
                    onChange={(e) => handleChange('incluyeGrupal', e.target.checked)}
                    className="h-4 w-4 rounded border-gray-300"
                  />
                  <div>
                    <span className="text-sm font-medium text-gray-700">👥 Clases grupales</span>
                    <p className="text-xs text-gray-400">Sesiones con horario fijo y capacidad máxima por clase</p>
                  </div>
                </label>
                {formData.incluyeGrupal && (
                  <div className="ml-7 pl-3 border-l-2 border-blue-200">
                    <HorariosBuilder
                      tipoSlot="grupal"
                      horarios={horarios.filter((h) => h.tipo_slot === 'grupal')}
                      showCapacity
                      onChange={(slots) =>
                        setHorarios((prev) => [...prev.filter((h) => h.tipo_slot !== 'grupal'), ...slots])
                      }
                    />
                  </div>
                )}
              </div>

              <div className="mb-4">
                <label className="flex items-center gap-3 mb-2">
                  <input
                    type="checkbox"
                    checked={formData.incluyePrivada}
                    onChange={(e) => handleChange('incluyePrivada', e.target.checked)}
                    className="h-4 w-4 rounded border-gray-300"
                  />
                  <div>
                    <span className="text-sm font-medium text-gray-700">👤 Clases privadas / individuales</span>
                    <p className="text-xs text-gray-400">Sesiones reservables 1 a 1</p>
                  </div>
                </label>
                {formData.incluyePrivada && (
                  <div className="ml-7 pl-3 border-l-2 border-purple-200">
                    <HorariosBuilder
                      tipoSlot="privada"
                      horarios={horarios.filter((h) => h.tipo_slot === 'privada')}
                      showCapacity={false}
                      onChange={(slots) =>
                        setHorarios((prev) => [...prev.filter((h) => h.tipo_slot !== 'privada'), ...slots])
                      }
                    />
                  </div>
                )}
              </div>
            </fieldset>
          )}

          {/* Listado */}
          {formData.tipoOferta === 'listado' && (
            <fieldset
              disabled={readOnlyForm}
              className="border-t pt-6 mt-6 min-w-0 border-x-0 border-b-0 border-gray-200"
            >
              <h3 className="text-lg font-medium text-gray-900 mb-4">🏠 Detalles del listado</h3>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Tipo de listado</label>
                  <select
                    value={formData.tipoListado}
                    onChange={(e) =>
                      handleChange('tipoListado', e.target.value as typeof formData.tipoListado)
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
                  >
                    <option value="">Seleccionar...</option>
                    <option value="venta">Venta</option>
                    <option value="arriendo">Arriendo</option>
                    <option value="oferta">Oferta especial</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Ubicación</label>
                  <input
                    type="text"
                    value={formData.ubicacionListado}
                    onChange={(e) => handleChange('ubicacionListado', e.target.value)}
                    placeholder="Barrio, Ciudad"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Área m² <span className="text-gray-400 font-normal">(opcional)</span>
                  </label>
                  <input
                    type="number"
                    value={formData.areaM2 ?? ''}
                    onChange={(e) =>
                      handleChange('areaM2', e.target.value ? Number(e.target.value) : null)
                    }
                    placeholder="80"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
                  />
                </div>
              </div>
              <p className="text-xs text-gray-500">
                Los datos del listado (habitaciones, marca, etc.) se configuran en la sección «Características»
                debajo de la descripción; puedes usar las plantillas rápidas allí cuando el tipo sea listado.
              </p>
            </fieldset>
          )}

          {/* Success Message */}
          {showSuccess && (
            <div className="fixed inset-0 bg-green-600 bg-opacity-90 flex items-center justify-center z-50">
              <div className="bg-white p-8 rounded-lg shadow-xl text-center">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <h3 className="text-lg font-medium text-gray-900 mb-2">
                  ¡Producto guardado exitosamente!
                </h3>
                <p className="text-gray-600">
                  {modo === 'crear' ? 'El producto ha sido agregado a tu inventario.' : 'Los cambios han sido guardados.'}
                </p>
              </div>
            </div>
          )}

          {/* Botones */}
          <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
            <button
              type="button"
              onClick={onCancelar}
              disabled={isSubmitting}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={isSubmitting || readOnlyForm}
              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
            >
              {isSubmitting ? (
                <>
                  <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Guardando...
                </>
              ) : (
                modo === 'crear' ? 'Crear' : 'Guardar Cambios'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ModalProducto;
